import { property, query, queryAll, queryAssignedElements, state } from 'lit/decorators.js';
import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { map } from "lit/directives/map.js";
import { SbkLabelMixin } from "@/components/label/label-mixin.ts";
import { FormControlDependencyMixin } from "@/components/form-control-dependency/form-control-dependency-mixin.ts";
import { FormValidationMixin } from "@/components/mixins/form-validation-mixin.ts";
import { when } from "lit/directives/when.js";
import { Validator } from "@/components/validators/validators.ts";

type Constructor<T> =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new (...args: any[]) => T;

// eslint-disable-next-line
declare const TYPO3: any;

export declare class AutocompleteMixinInterface {
    autocompleteDisabled: boolean;
    suggestions: SuggestItem[];
    value: string;
    invalid: boolean;
    renderSuggestions(): TemplateResult<1>;
    _querySuggestions(inputValue: string): Promise<void>;
    close(force: boolean): void;
    getValidators(): Validator[];
    handleErrors(validators: Validator[], inputValue: string): void;
}

export type SuggestItem = {
    label: string
    value: string
};

export const AutocompleteMixin = <T extends Constructor<LitElement>>(superClass: T) => {
    class AutocompleteMixin extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(superClass))) {

        static formAssociated = true;

        static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

        _internals: ElementInternals;

        @property({ attribute: 'autocomplete-disabled' })
        autocompleteDisabled: boolean = false;

        @property({ type: Boolean })
        disabled: boolean = false

        @property()
        description: string = ''

        @property()
        name: string = ''

        @property()
        placeholder: string = ''

        @property({ type: Boolean })
        readonly: boolean = false

        @property({ type: Boolean })
        required: boolean = false

        @property()
        size?: 'large' | 'small' = 'large';

        @property()
        value: string = ''

        @state()
        visible: boolean = true

        @queryAssignedElements({ slot: 'additionalInputs' })
        _assignedElements!: Array<HTMLElement>;

        @state()
        _expanded = false;

        @state()
        suggestions: SuggestItem[] = [];

        @query('input')
        _combobox!: HTMLInputElement

        @query('[role="listbox"]')
        _listbox!: HTMLElement

        @queryAll('[role="option"]:not([disabled])')
        _optionNodes!: HTMLElement[]

        listboxHasVisualFocus = false;
        comboboxHasVisualFocus = false;

        option: HTMLElement | null = null;

        firstOption: HTMLElement | null = null;

        lastOption: HTMLElement | null = null;

        isList = true;
        isNone = false;
        isBoth = false;
        hasHover = false;

        selectOnlyOption = false;

        constructor() {
            super();
            this._internals = this.attachInternals();
            // @ts-expect-error wrong return type
            this.onComboboxInput = this.debounce(this.onComboboxInput.bind(this), 275);
        }

        willUpdate(changedProperties: PropertyValues<this>) {
            // only need to check changed properties for an expensive computation.
            if (changedProperties.has('value')) {
                this._internals.setFormValue(this.value);
                const selectValueChangedEvent = new CustomEvent('input-value-changed',
                    {
                        detail: { value: this.value },
                        bubbles: true
                    }
                );
                this.dispatchEvent(selectValueChangedEvent);
            }
        }

        connectedCallback() {
            super.connectedCallback();
            this._internals.setFormValue(this.value);
            document.body.addEventListener(
                'pointerup',
                this.onBackgroundPointerUp,
                true
            );
        }

        disconnectedCallback() {
            document.body.removeEventListener(
                'pointerup',
                this.onBackgroundPointerUp,
                true
            );
            super.disconnectedCallback();
        }

        render() {
            return html`
                <div class="input__wrapper${this.invalid ? ' input__wrapper--error' : ''}${this.disabled || this.readonly ? ' input__wrapper--disabled' : ''}">
                    <div class="autocomplete__wrapper">
                        ${this.labelTemplate(`${this.name}-input`, false, this.invalid)} 
                        ${this.autocompleteDisabled ? this.renderStandardInput() : this.renderAutocompleteInput()}
                    </div>
                    ${when(this.description && !this.invalid, () => html`
                        <sbk-annotation id="annotation" variant="info" ?disabled=${this.disabled || this.readonly}>${this.description}</sbk-annotation>
                    `)} 
                    ${this.invalid ? this.validationErrorTemplate("annotation") : ""}
                </div>
            `;
        }

        renderStandardInput() {
            return html`
                <input id="${this.name}-input" 
                       class="input input--large" 
                       name=${this.name} 
                       placeholder=${this.placeholder} 
                       .value=${this.value}
                       @change=${this.onComboboxChange}
                       @blur=${this.onComboboxBlur} />
                ${this.renderSuggestions()}
            `;
        }

        renderAutocompleteInput() {
            return html`
                <input id="${this.name}-input" 
                       class="input input--large" 
                       name=${this.name} 
                       placeholder=${this.placeholder} 
                       .value=${this.value} 
                       @input=${this.onComboboxInput} 
                       @keydown=${this.onComboboxKeyDown}
                       @change=${this.onComboboxChange} 
                       @focus=${this.onComboboxFocus} 
                       @blur=${this.onComboboxBlur} 
                       @click=${this.onComboboxClick} 
                       role="combobox" 
                       aria-expanded=${this._expanded} 
                       autocomplete="off" 
                       aria-autocomplete="list" 
                       aria-controls="listbox" />
                ${this.renderSuggestions()}
            `;
        }

        renderSuggestions() {
            if (this._combobox && this._combobox.value.length > 0 && this.suggestions.length === 0) {
                return html`
                <ul role="listbox"
                    @pointerover=${this.onListboxPointerover}
                    @pointerout=${this.onListboxPointerout}
                    id="listbox"
                    aria-label="Autocomplete"
                    class="autocomplete-items single">
                        <li id="option"
                                    role="option"
                                    class="autocomplete-item"
                                    @click=${this.onNotFoundOptionClick}
                                    disabled><span>${this._getTYPO3Label('noMatchesFound', 'Keine Treffer gefunden')}</span></li>
                </ul>
            `;
            }
            return html`
                <ul role="listbox"
                    @pointerover=${this.onListboxPointerover}
                    @pointerout=${this.onListboxPointerout}
                    id="listbox"
                    aria-label="Autocomplete"
                    class="autocomplete-items ${this.suggestions.length === 1 ? 'single' : ''}">
                    ${map(this.suggestions, (suggestion: SuggestItem, index: number) => html`
                                <li id="option${index + 1}"
                                    role="option"
                                    class="autocomplete-item"
                                    @click=${this.onOptionClick}
                                    @pointerover=${this.onOptionPointerover}
                                    @pointerout=${this.onOptionPointerout}
                                    data-suggestion=${suggestion.value}><span>${unsafeHTML(this._decorateAutosuggestion(suggestion.label))}</span></li>
                    `
            )}
                </ul>
            `;
        }

        _decorateAutosuggestion(label: string) {
            const pattern = new RegExp(this._combobox.value, 'gi');
            return label.replace(
                pattern,
                '<strong>$&</strong>'
            );
        }

        _getTYPO3Label(key: string, defaultLabel: string): string {
            return typeof TYPO3 !== "undefined" ? TYPO3.lang[key] : defaultLabel;
        }

        debounce<T extends (...args: Parameters<T>) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
            let timeout: ReturnType<typeof setTimeout>;
            return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), wait);
            };
        }

        async _querySuggestions(inputValue: string) {
            console.log(inputValue);
            await this.updateComplete;
        }

        getLowercaseContent(node: HTMLElement): string {
            return node.dataset.suggestion?.toLowerCase() ?? '';
        }

        isOptionInView(option: HTMLElement) {
            const bounding = option.getBoundingClientRect();
            return (
                bounding.top >= 0 &&
                bounding.left >= 0 &&
                bounding.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) &&
                bounding.right <=
                (window.innerWidth || document.documentElement.clientWidth)
            );
        }

        isPointerWithinElement(event: PointerEvent, element: HTMLElement) {
            const rect = element.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;

            return (
                x >= rect.left &&
                x <= rect.right &&
                y >= rect.top &&
                y <= rect.bottom
            );
        }

        setActiveDescendant(option: HTMLElement | false) {
            if (option && this.listboxHasVisualFocus) {
                this._combobox.setAttribute('aria-activedescendant', option.id);
                if (!this.isOptionInView(option)) {
                    option.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } else {
                this._combobox.setAttribute('aria-activedescendant', '');
            }
        }

        setValue(value: string) {
            this._combobox.value = value;
            this.value = value;
            this._combobox.setSelectionRange(value.length, value.length);
        }

        setOption(option: HTMLElement | null, flag: boolean = false) {
            if (option) {
                this.option = option;
                this.setCurrentOptionStyle(this.option);
                this.setActiveDescendant(this.option);

                if (this.isBoth) {
                    this._combobox.value = this.option.dataset.suggestion ?? '';
                    this.value = this.option.dataset.suggestion ?? '';
                    if (flag) {
                        if (this.option.dataset.suggestion) {
                            this._combobox.setSelectionRange(
                                this.option.dataset.suggestion.length,
                                this.option.dataset.suggestion.length
                            );
                        }
                    } else {
                        if (this.option.dataset.suggestion) {
                            this._combobox.setSelectionRange(
                                this._combobox.value.length,
                                this.option.dataset.suggestion.length
                            );
                        }
                    }
                }
            }
        }

        setVisualFocusCombobox() {
            this._listbox.classList.remove('focus');
            this._combobox.classList.add('focus'); // set the focus class to the parent for easier styling
            this.comboboxHasVisualFocus = true;
            this.listboxHasVisualFocus = false;
            this.setActiveDescendant(false);
        }

        setVisualFocusListbox() {
            this._combobox.classList.remove('focus');
            this.comboboxHasVisualFocus = false;
            this.listboxHasVisualFocus = true;
            this._listbox.classList.add('focus');
            this.option && this.setActiveDescendant(this.option);
        }

        removeVisualFocusAll() {
            this._combobox.classList.remove('focus');
            this.comboboxHasVisualFocus = false;
            this.listboxHasVisualFocus = false;
            this._listbox.classList.remove('focus');
            this.option = null;
            this.setActiveDescendant(false);
        }

        // ComboboxAutocomplete Events

        async filterOptions() {
            await this._querySuggestions(this._combobox.value);
            let option = null;
            const currentOption = this.option;

            // Use populated options array to initialize firstOption and lastOption.
            const numItems = this._optionNodes.length;

            if (numItems > 0) {
                this.firstOption = this._optionNodes[0];
                this.lastOption = this._optionNodes[numItems - 1];

                const optionNodes = [...this._optionNodes];

                if (currentOption && optionNodes.indexOf(currentOption) >= 0) {
                    option = currentOption;
                } else {
                    option = this.firstOption;
                }
            } else {
                this.firstOption = null;
                option = null;
                this.lastOption = null;
            }

            return option;
        }

        setCurrentOptionStyle(option: HTMLElement | boolean) {
            for (let i = 0; i < this._optionNodes.length; i++) {
                const opt = this._optionNodes[i];
                if (opt === option) {
                    opt.setAttribute('aria-selected', 'true');
                    if (
                        this._listbox.scrollTop + this._listbox.offsetHeight <
                        opt.offsetTop + opt.offsetHeight
                    ) {
                        this._listbox.scrollTop =
                            opt.offsetTop + opt.offsetHeight - this._listbox.offsetHeight;
                    } else if (this._listbox.scrollTop > opt.offsetTop + 2) {
                        this._listbox.scrollTop = opt.offsetTop;
                    }
                } else {
                    opt.removeAttribute('aria-selected');
                }
            }
        }

        getPreviousOption(currentOption: HTMLElement | null) {
            if (currentOption && currentOption !== this.firstOption) {
                const arr = Array.from(this._optionNodes);
                const index = arr.indexOf(currentOption);
                return arr[index - 1];
            }
            return this.lastOption;
        }

        getNextOption(currentOption: HTMLElement | null) {
            if (currentOption && currentOption !== this.lastOption) {
                const arr = Array.from(this._optionNodes);
                const index = arr.indexOf(currentOption);
                return arr[index + 1];
            }
            return this.firstOption;
        }

        /* MENU DISPLAY METHODS */

        doesOptionHaveFocus() {
            return this._combobox.getAttribute('aria-activedescendant') !== '';
        }

        isOpen() {
            return this._listbox.style.display === 'block';
        }

        isClosed() {
            return this._listbox.style.display !== 'block';
        }

        hasOptions() {
            return this._optionNodes.length;
        }

        open() {
            this._listbox.style.display = 'block';
            this._expanded = true;
        }

        close(force: boolean = false) {
            if (
                force ||
                (!this.comboboxHasVisualFocus &&
                    !this.listboxHasVisualFocus &&
                    !this.hasHover)
            ) {
                this.setCurrentOptionStyle(false);
                this._listbox.style.display = 'none';
                this._expanded = false;
                this.setActiveDescendant(false);
                this._combobox.classList.add('focus');
            }
        }

        /* combobox Events */
        onComboboxChange(event: Event) {
            const element = <HTMLInputElement>event.target;
            this._internals.setFormValue(element.value);
            this.value = element.value;
            const selectValueChangedEvent = new CustomEvent('input-value-changed',
                {
                    detail: { value: this.value },
                    bubbles: true
                }
            );
            this.dispatchEvent(selectValueChangedEvent);
        }

        async onComboboxKeyDown(event: KeyboardEvent) {
            let flag = false;
            const altKey = event.altKey;

            if (event.ctrlKey || event.shiftKey) {
                return;
            }

            switch (event.key) {
                case 'Enter':
                    if (this.listboxHasVisualFocus) {
                        this.setValue(this.option?.dataset.suggestion ?? '');
                    }
                    this.close(true);
                    this.setVisualFocusCombobox();
                    flag = true;
                    break;

                case 'Down':
                case 'ArrowDown':
                    if (this._optionNodes.length > 0) {
                        if (altKey) {
                            this.open();
                        } else {
                            this.open();
                            if (
                                this.listboxHasVisualFocus ||
                                (this.isBoth && this._optionNodes.length > 1)
                            ) {
                                this.setOption(this.getNextOption(this.option), true);
                                this.setVisualFocusListbox();
                            } else {
                                this.setOption(this.firstOption, true);
                                this.setVisualFocusListbox();
                            }
                        }
                    }
                    flag = true;
                    break;

                case 'Up':
                case 'ArrowUp':
                    if (this.hasOptions()) {
                        if (this.listboxHasVisualFocus) {
                            this.setOption(this.getPreviousOption(this.option), true);
                        } else {
                            this.open();
                            if (!altKey) {
                                this.setOption(this.lastOption, true);
                                this.setVisualFocusListbox();
                            }
                        }
                    }
                    flag = true;
                    break;

                case 'Esc':
                case 'Escape':
                    if (this.isOpen()) {
                        this.close(true);
                        await this.filterOptions();
                        this.setVisualFocusCombobox();
                    } else {
                        this.setValue('');
                        this._combobox.value = '';
                        this.value = '';
                    }
                    this.option = null;
                    flag = true;
                    break;

                case 'Tab':
                    this.close(true);
                    if (this.listboxHasVisualFocus) {
                        if (this.option) {
                            this.setValue(this.option?.dataset.suggestion ?? '');
                        }
                    }
                    break;

                case 'Home':
                    this._combobox.setSelectionRange(0, 0);
                    flag = true;
                    break;

                case 'End':
                    this._combobox.setSelectionRange(this._combobox.value.length, this._combobox.value.length);
                    flag = true;
                    break;

                default:
                    break;
            }

            if (flag) {
                event.stopPropagation();
                event.preventDefault();
            }
        }

        isPrintableCharacter(str: string) {
            return str.length === 1 && str.match(/\S| /);
        }

        async onComboboxInput(event: InputEvent) {
            let flag = false,
                option = null;

            this.setVisualFocusCombobox();
            this.setCurrentOptionStyle(false);
            flag = true;

            if (this.isList || this.isBoth) {
                option = await this.filterOptions();
                if (option) {
                    if (this.isClosed() && this._combobox.value.length) {
                        this.open();
                    }

                    if (
                        this.getLowercaseContent(option).indexOf(
                            this._combobox.value.toLowerCase()
                        ) === 0
                    ) {
                        this.option = option;
                        if (this.isBoth || this.listboxHasVisualFocus) {
                            this.setCurrentOptionStyle(option);
                            if (this.isBoth) {
                                this.setOption(option);
                            }
                        }
                    } else {
                        this.option = null;
                        this.setCurrentOptionStyle(false);
                    }
                } else {
                    this.close();
                    this.option = null;
                    this.setActiveDescendant(false);
                }
            } else if (this._combobox.value.length) {
                this.open();
            }

            if (flag) {
                event.stopPropagation();
                event.preventDefault();
            }
        }

        onComboboxClick() {
            if (this._optionNodes.length === 0) {
                return;
            }
            if (this.isOpen()) {
                this.close(true);
            } else {
                this.open();
            }
        }

        async onComboboxFocus() {
            this.setVisualFocusCombobox();
            this.option = null;
            this.setCurrentOptionStyle(false);
            await this.filterOptions();
            if (this.suggestions.length) {
                if (this.suggestions.length === 1) {
                    this.setValue(this.firstOption?.dataset.suggestion ?? '');
                } else {
                    this.open();
                }
            }
        }

        onComboboxBlur() {
            this.removeVisualFocusAll();
            const validators = this.getValidators();
            this.handleErrors(validators, this._combobox.value);
        }

        onBackgroundPointerUp = (event: PointerEvent) => {
            if (event.target && event.target instanceof Node &&
                !this.isPointerWithinElement(event, this._combobox) &&
                !this.isPointerWithinElement(event, this._listbox)
            ) {
                this.comboboxHasVisualFocus = false;
                this.setCurrentOptionStyle(false);
                this.removeVisualFocusAll();
                this.close(true);
            }
        }

        /* Listbox Events */

        onListboxPointerover() {
            this.hasHover = true;
        }

        onListboxPointerout() {
            this.hasHover = false;
            setTimeout(this.close.bind(this, false), 300);
        }

        // Listbox Option Events

        onOptionClick(event: Event) {
            if (event.target && event.target instanceof HTMLElement) {
                let option: HTMLElement | null = event.target;
                if (!event.target.classList.contains('autocomplete-item')) {
                    option = option.closest('.autocomplete-item');
                }
                this._combobox.value = option?.dataset.suggestion ?? '';
                this.value = option?.dataset.suggestion ?? '';
                this.close(true);
            }
        }

        onNotFoundOptionClick(event: Event) {
            if (event.target && event.target instanceof HTMLElement) {
                this.value = this._combobox.value;
                this.close(true);
            }
        }

        onOptionPointerover() {
            this.hasHover = true;
            this.open();
        }

        onOptionPointerout() {
            this.hasHover = false;
            setTimeout(this.close.bind(this, false), 300);
        }
    }
    return AutocompleteMixin as Constructor<AutocompleteMixinInterface> & T;
}